'use client'

import { useEffect, useState } from 'react'
import type { ChatState } from '@/hooks/use-postgraduate-chat'
import { CHAT_FLOW, type ChatStep, type ChatStepId } from '@/lib/postgraduate-chat/chat-flow'
import { ChatComposer } from './chat-composer'
import { ChatHeader } from './chat-header'
import { ChatStartCard } from './chat-start-card'
import { ChatTranscript } from './chat-transcript'
import { ChatWhatsappCta } from './chat-whatsapp-cta'

interface ChatPanelProps {
  readonly attendantName: string
  readonly whatsappNumber: string
  readonly state: ChatState
  readonly currentStep: ChatStep | null
  readonly answer: (stepId: ChatStepId, value: string) => void
  readonly selectOption: (stepId: ChatStepId, value: string) => void
  readonly start: () => void
  readonly retry: () => void
  readonly close: () => void
}

/** Painel do chat comercial de pós-graduação (HN-3170) — header, transcript, composer e fases. */
export function ChatPanel({
  attendantName,
  whatsappNumber,
  state,
  currentStep,
  answer,
  selectOption,
  start,
  retry,
  close,
}: ChatPanelProps) {
  const [transcriptReady, setTranscriptReady] = useState(false)

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || state.phase === 'enviando') {
        return
      }
      close()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [state.phase, close])

  const composerDisabled =
    state.phase !== 'perguntando' || currentStep?.answerType === 'options'

  return (
    <div
      aria-label={`Chat com ${attendantName}`}
      aria-modal="false"
      className="fixed inset-0 z-50 flex h-dvh flex-col overflow-hidden bg-white sm:inset-auto sm:right-6 sm:bottom-6 sm:h-[600px] sm:max-h-[85dvh] sm:w-[380px] sm:rounded-2xl sm:border sm:border-neutral-200 sm:shadow-[0_20px_50px_rgba(12,34,143,0.18)]"
      role="dialog"
    >
      <ChatHeader
        attendantName={attendantName}
        closeDisabled={state.phase === 'enviando'}
        onClose={close}
        whatsappNumber={whatsappNumber}
      />

      <div className="min-h-0 flex-1">
        <ChatTranscript
          answers={state.answers}
          onInteractionReadyChange={setTranscriptReady}
          onSelectOption={selectOption}
          phase={state.phase}
          transcript={state.transcript}
        />
      </div>

      {state.phase === 'saudacao' && transcriptReady && <ChatStartCard onStart={start} />}

      {state.phase === 'perguntando' && transcriptReady && (
        <ChatComposer
          answerType={currentStep?.answerType ?? null}
          disabled={composerDisabled}
          onSubmit={answer}
          stepId={currentStep?.id ?? null}
        />
      )}

      {state.phase === 'concluido' && (
        <ChatWhatsappCta answers={state.answers} whatsappNumber={whatsappNumber} />
      )}

      {state.phase === 'erroEnvio' && (
        <div className="flex shrink-0 flex-col items-center gap-2 border-neutral-200 border-t p-3">
          <p className="text-center font-rethink text-neutral-800 text-sm">
            {state.submissionError ?? 'Não conseguimos enviar suas informações agora.'}
          </p>
          <button
            className="rounded-full bg-primary-800 px-4 py-2 font-rethink font-semibold text-sm text-white transition-colors hover:bg-primary-800/90"
            onClick={retry}
            type="button"
          >
            Tentar novamente
          </button>
        </div>
      )}
    </div>
  )
}

export const POSTGRADUATE_CHAT_TOTAL_STEPS = CHAT_FLOW.length
